import { computed, onMounted, ref, Ref, RendererElement } from "vue";
import {
  DataFragment,
  DataManager,
  DataSlide,
  FragmentOptions,
  PretzelSlideDefinition,
  SlideDefinitionProps,
  SlideOptions,
  State
} from "@/front-tools/pretzel/types.ts";

let MAX_PRETZEL_ID = 0;

export namespace Fragments {
  const DATA_VISIBLE = 'visible';
  const DATA_FRAGMENT_ID = 'fragmentId';
  const DATA_FRAGMENT_INDEX = 'fragmentIndex';
  const DATA_AUTOSLIDE = 'autoslide';
  const DATA_DURATION = 'duration';
  const DATA_CURRENT = 'current';

  export function setVisible(element: HTMLElement, value: boolean, noCurrent: boolean = false): void {
    if (value) {
      if(!noCurrent){
        if(!element.dataset[DATA_VISIBLE]) 
          element.dataset[DATA_CURRENT] = 'true' 
        else 
          delete element.dataset[DATA_CURRENT]
      }
      element.dataset[DATA_VISIBLE] = 'true';
    } else {
      delete element.dataset[DATA_VISIBLE]
    }
  }

  export function setFragmentId(element: HTMLElement | RendererElement, value: string): void {
    element.dataset[DATA_FRAGMENT_ID] = value;
  }

  export function setFragmentIndex(element: HTMLElement | RendererElement, value: number): void {
    element.dataset[DATA_FRAGMENT_INDEX] = value.toString();
  }

  export function getFragmentIndex(element: HTMLElement | RendererElement): number | undefined {
    const rawValue = element.dataset[DATA_FRAGMENT_INDEX];
    if (rawValue === undefined) return;
    const parsedValue = parseInt(rawValue, 10);
    if (isNaN(parsedValue)) return;
    return parsedValue;
  }

  export function setFragmentAutoslide(element: HTMLElement | RendererElement, value: number): void {
    element.dataset[DATA_AUTOSLIDE] = value.toString();
  }

  export function getFragmentAutoslide(element: HTMLElement, defaultAutoslide: number): number {
    const rawValue = element.dataset[DATA_AUTOSLIDE];
    if (rawValue === undefined) return defaultAutoslide;
    const parsedValue = parseInt(rawValue, 10);
    if (isNaN(parsedValue)) return defaultAutoslide;
    return parsedValue;
  }

  export function setFragmentDuration(element: HTMLElement | RendererElement, value: number): void {
    element.dataset[DATA_DURATION] = value.toString();
  }

  export function getVisible(element: HTMLElement): boolean {
    return !!element.dataset[DATA_VISIBLE];
  }
}

namespace Elements {
  export function getRoot(managerId: string): undefined | HTMLElement {
    return document.querySelector<HTMLElement>(`[data-pretzel-id="${managerId}"]`) ?? undefined;
  }

  export function getRootSlides(managerId: string) {
    return Array.from(document.querySelectorAll<HTMLElement>(`[data-pretzel-id="${managerId}"] [data-slide-id]`));
  }

  export function getSlideFragments(element: HTMLElement) {
    return Array.from(element.querySelectorAll<HTMLElement>('[data-fragment]'))
  }
}

abstract class SlideAccesser<SlideId> {
  protected abstract manager: PretzelManager<SlideId>;
  protected abstract slideOptions: SlideOptions<SlideId>;

  public toString(): string {
    if ('index' in this.slideOptions) {
      return `Pretzel["${this.manager.id}"].slide(index=${this.slideOptions.index})`;
    } else {
      return `Pretzel["${this.manager.id}"].slide(id="${this.slideOptions.id}")`;
    }
  }

  protected get slideIndex(): number {
    if ('index' in this.slideOptions) {
      return this.slideOptions.index;
    } else {
      const id = this.slideOptions.id
      const index = this.manager.slides.findIndex(slide => slide.id === id);
      if (index === -1) throw new Error(`${this.toString()} : slide not found`);
      return index;
    }
  }

  protected get slide(): DataSlide<SlideId> {
    if ('index' in this.slideOptions) {
      const index = this.slideOptions.index;
      if (index === -1) throw new Error(`${this.toString()} : slide not found`);
      return this.manager.slides[index];
    } else {
      const id = this.slideOptions.id
      const slide = this.manager.slides.find(slide => slide.id === id);
      if (slide === undefined) throw new Error(`${this.toString()} : slide not found`);
      return slide;
    }
  }

}

export class SlideProxy<SlideId = unknown> extends SlideAccesser<SlideId> {
  constructor(protected manager: PretzelManager<SlideId>, protected slideOptions: SlideOptions<SlideId>) {
    super();
  }

  nextSlide(delta: number = 1): SlideProxy<SlideId> {
    return new SlideProxy<SlideId>(this.manager, { index: this.slideIndex + delta });
  }

  prevSlide(delta: number = 1): SlideProxy<SlideId> {
    return new SlideProxy<SlideId>(this.manager, { index: this.slideIndex - delta });
  }

  get lastFragment(): FragmentProxy<SlideId> {
    const slide = this.slide;
    if (slide.fragments.length === 0) throw new Error(`${this.toString()}.lastFragment : no fragments found`);
    return new FragmentProxy<SlideId>(this.manager, this.slideOptions, { index: slide.fragments.length - 1 });
  }

  get firstFragment(): FragmentProxy<SlideId> {
    const slide = this.slide;
    if (slide.fragments.length === 0) throw new Error(`${this.toString()}.lastFragment : no fragments found`);
    return new FragmentProxy<SlideId>(this.manager, this.slideOptions, { index: 0 });
  }

  get slideItem(): DataSlide<SlideId> {
    return this.slide
  }

  fragment(id: string): FragmentProxy<SlideId> {
    const slideIndex = this.slideIndex;
    if (slideIndex === -1) throw new Error(`${this.toString()} : slide not found`);
    const slide = this.manager.slides[slideIndex];
    const fragmentIndex = slide.fragments.findIndex(fragment => fragment.id === id);
    return new FragmentProxy<SlideId>(this.manager, this.slideOptions, { index: fragmentIndex }) // TODO
  }

  moveTo(): void {
    this.manager.setState({ slideIndex: this.slideIndex });
  }
}

export class FragmentProxy<SlideId = unknown, FragmentId = unknown> extends SlideAccesser<SlideId> {
  constructor(
    protected manager: PretzelManager<SlideId>,
    protected slideOptions: SlideOptions<SlideId>,
    protected fragmentOptions: FragmentOptions<FragmentId>
  ) {
    super();
  }

  protected get fragmentIndex(): number {
    if ('index' in this.fragmentOptions) {
      return this.fragmentOptions.index;
    } else {
      const id = this.fragmentOptions.id
      const index = this.slide.fragments.findIndex(fragment => fragment.id === id);
      if (index === -1) throw new Error(`${this.toString()} : slide not found`);
      return index;
    }
  }

  prevFragment(delta: number = 1): FragmentProxy<SlideId, FragmentId> {
    return new FragmentProxy<SlideId, FragmentId>(this.manager, this.slideOptions, { index: this.fragmentIndex + delta });
  }

  nextFragment(delta: number = 1): FragmentProxy<SlideId, FragmentId> {
    return new FragmentProxy<SlideId, FragmentId>(this.manager, this.slideOptions, { index: this.fragmentIndex - delta });
  }

  moveTo(): void {
    this.manager.setState({ slideIndex: this.slideIndex, fragmentIndex: this.fragmentIndex });
  }
}

export class PretzelManager<
  SlideId = unknown,
  StateId = unknown,
  Props = unknown,
  DataManager = unknown,
> {
  id: string;
  children: PretzelSlideDefinition<SlideId, StateId, Props, DataManager>[];
  initialized: boolean;
  private maxStateIndex: number;
  currentSlideIndex: number;
  currentFragmentIndex: number;
  slides: DataSlide<SlideId, Props>[];
  states: State<SlideId, StateId, Props, DataManager>[];
  fastMode: boolean;


  private slideChangedCallbacks: ((slide: DataSlide<SlideId, Props>) => void)[];
  private slideTransitionEndCallbacks: ((slide: DataSlide<SlideId, Props>) => void)[];
  private fragmentShownCallbacks: ((slide: DataFragment) => void)[];
  private timeout: number;
  private options: {
    duration: number;
    autoslide: number;
    fastMode?:boolean;
  };
  public paused: boolean;

  constructor(props: {
    children: SlideDefinitionProps<SlideId, StateId, Props>[];
    options: {
      duration: number;
      autoslide: number;
      fastMode?:boolean;
    }
  }) {
    this.id = `pretzel-manager-${MAX_PRETZEL_ID}`;
    MAX_PRETZEL_ID++;
    this.children = props.children.map(child => ({
      id: child.id,
      component: child.component,
      props: child.props,
      states: [],
    }));
    this.maxStateIndex = 0;
    this.currentSlideIndex = 0;
    this.currentFragmentIndex = -1;
    this.initialized = false;
    this.slides = [];
    this.states = [];
    this.options = props.options;
    this.paused = false;

    this.slideChangedCallbacks = [];
    this.slideTransitionEndCallbacks = [];
    this.fragmentShownCallbacks = [];
    this.timeout = -1;

    this.fastMode = props.options.fastMode || false
  }

  getFragments(slideId: SlideId) {

    const pretzelElement = Elements.getRoot(this.id);
    if (!pretzelElement) {
      console.error(`pretzel container element not found !`);
      return [];
    }

    return Array.from(pretzelElement.querySelectorAll(`[data-fragment]`));
  }

  initialize() {
    if (this.initialized) throw new Error(`cannot initialize twice !`);
    this.initialized = true;
    this.sync('on initialized');
  }

  addState(childId: SlideId, stateProps: {
    id?: StateId;
    fragmentId?: string;
    props: Partial<Props>;
    on: (manager: DataManager) => boolean;
  }) {
    const child = this.children.find(child => child.id === childId);
    if (!child) throw new Error('Pretzel configuration error - childId doesn\'t exist in Pretzel children');
    const index = this.maxStateIndex;
    this.maxStateIndex++;

    const state: State<SlideId, StateId, Props, DataManager> = {
      id: stateProps.id ? stateProps.id : `${childId}-${index}` as StateId,
      index: index,
      slideId: childId,
      fragmentId: stateProps.fragmentId,
      props: { ...child.props, ...(stateProps.props ?? {}) } as Props,
      on: stateProps.on,
    };

    child.states.push(state)
    this.states.push(state);
  }

  private sortFragments(elements: HTMLElement[]): HTMLElement[] {
    return elements.sort((a, b) => {
      const aIndex = Fragments.getFragmentIndex(a);
      const bIndex = Fragments.getFragmentIndex(b);

      if (aIndex === undefined && bIndex === undefined) return 0;
      if (aIndex === undefined) return 1;
      if (bIndex === undefined) return -1;
      return aIndex - bIndex;
    });
  }

  private isFragmentCurrent(slideIndex: number, fragmentIndex: number): boolean {
    return slideIndex === this.currentSlideIndex && fragmentIndex === this.currentFragmentIndex;
  }

  private isFragmentVisible(slideIndex: number, fragmentIndex: number): boolean {
    if (slideIndex < this.currentSlideIndex) return true;
    if (slideIndex === this.currentSlideIndex) return fragmentIndex <= this.currentFragmentIndex;
    return false;
  }

  private getDOMFragment(fragmentElement: HTMLElement, slideIndex: number, fragmentIndex: number) {
    return {
      id: fragmentElement.dataset.fragmentId ?? '-',
      element: fragmentElement,
      visible: this.isFragmentVisible(slideIndex, fragmentIndex),
      active: this.isFragmentCurrent(slideIndex, fragmentIndex),
      autoslide: Fragments.getFragmentAutoslide(fragmentElement, this.options.autoslide),
    };
  }

  private getDOMSlide(slideElement: HTMLElement, slideIndex: number): DataSlide<SlideId> {
    const slideId = (slideElement.dataset.slideId ?? '-') as SlideId;

    const child = this.children.find(child => child.id === slideId);
    if (child === undefined) throw new Error(`slide[id=${slideId}] exists in dom without definition in manager.`);

    return {
      id: slideId,
      element: slideElement,
      fragments: this.sortFragments(Elements.getSlideFragments(slideElement))
        .map((fragmentElement, fragmentIndex) =>
          this.getDOMFragment(fragmentElement, slideIndex, fragmentIndex)
        ),
      duration: child.duration ?? this.options.duration,
      props: child.props,
    };
  }

  /**
   * This will scan the current dom and memoize the data about the current dom state.
   * @private
   */
  private scan() {
    this.slides.splice(0, this.slides.length);
    this.slides.push(
      ...Elements.getRootSlides(this.id)
        .map((slideElement, slideIndex) => this.getDOMSlide(slideElement, slideIndex))
    );
  }

  private cancelTimeout() {
    if (this.timeout !== -1) {
      clearTimeout(this.timeout);
      this.timeout = -1;
    }
  }

  private triggerSlideTransition(slide: DataSlide<SlideId>): void {
    if (this.paused) return;
    this.cancelTimeout();
    this.timeout = window.setTimeout(() => {
      this.slideTransitionEndCallbacks.forEach(callback => callback(slide));
      this.next()
    }, this.fastMode ? 100 : slide.duration);
  }

  private triggerFragmentTransition(fragment: DataFragment) {
    if (this.paused) return;
    if (fragment && fragment.autoslide >= 0) {
      this.cancelTimeout();
      this.timeout = window.setTimeout(() => {
        this.next()
      }, this.fastMode ? 100 : (fragment.autoslide || 1));
    }
    if (fragment && fragment.autoslide < 0){
      this.cancelTimeout();
    }
  }

  public sync(message: string = '') {
    if (message !== '') console.log('SYNC', message)
    this.scan();

    const activeSlide = this.slides[this.currentSlideIndex];
    if (!activeSlide) return;

    activeSlide.fragments.forEach((fragment, index) => {
      Fragments.setVisible(fragment.element, fragment.visible, this.currentFragmentIndex === activeSlide.fragments.length - 1);
      if (fragment.active) this.fragmentShownCallbacks.forEach(callback => callback(fragment));
    })

    if (this.currentFragmentIndex === -1) return this.triggerSlideTransition(activeSlide);

    const activeFragment = activeSlide.fragments[this.currentFragmentIndex];
    if (!activeFragment) return;

    return this.triggerFragmentTransition(activeFragment);
  }

  private getContainerElement() {
    return document.querySelector(`[data-pretzel-id="${this.id}"]`);
  }

  private getFragment(id?: SlideId, index?: number) {
    const containerElement = this.getContainerElement();
    if (!containerElement) return null;
    const slide = id ? this.children.find(child => child.id === id) : this.children[this.currentSlideIndex];
    if (!slide) return null;
    const slideElement = containerElement.querySelector(`[data-slide-id="${slide.id}"]`);
    if (!slideElement) return null;
    const fragmentElements = Array.from(slideElement.querySelectorAll(`[data-fragment]`));
    index = index ?? this.currentFragmentIndex;
    if (index < 0 || index >= fragmentElements.length) return null;
    return fragmentElements[index] as HTMLElement;
  }

  private nextSlide() {
    this.currentSlideIndex++;
    const newCurrentSlide = this.slides[this.currentSlideIndex];
    this.currentFragmentIndex = -1;
    if (newCurrentSlide) this.slideChangedCallbacks.forEach(callback => callback(newCurrentSlide));
  }

  private nextFragment() {
    this.currentFragmentIndex++;
  }

  public togglePause(isPaused: boolean) {
    this.paused = isPaused;
    if (isPaused) {
      clearTimeout(this.timeout);
    } else {
      this.sync();
    }
  }


  /**
   * Upon calling this we go to the next slide.
   */
  next(msg: string = '') {
    if (msg !== '') console.log('NEXT', msg)
    const currentSlide = this.children[this.currentSlideIndex];
    if (!currentSlide) return;

    const nextFragmentElement = this.getFragment(currentSlide.id, this.currentFragmentIndex + 1);
    if (nextFragmentElement !== null) {
      this.nextFragment();
    } else if (this.currentSlideIndex < this.slides.length - 1) {
      this.nextSlide();
    }
    this.sync();
  }

  getState(): { slideIndex: number; fragmentIndex: number; } {
    return { slideIndex: this.currentSlideIndex, fragmentIndex: this.currentFragmentIndex };
  }

  setState(state: { slideIndex: number; fragmentIndex?: number; } | { stateId: StateId }) {
    this.cancelTimeout()
    let newCurrentSlide
    if ('stateId' in state) {
      const id = state.stateId;
      let slideIndex = 0;
      for (const child of this.children) {
        for (const state of child.states) {
          if (state.id === id) {
            const fragmentIndex = state.fragmentId ? this.slides[slideIndex].fragments.findIndex(f => f.id === state.fragmentId) : 0;
            newCurrentSlide = this.slides[slideIndex];
            return this.setState({ slideIndex: slideIndex, fragmentIndex: fragmentIndex });
          }
        }
        slideIndex++;
      }
    } else {
      this.currentSlideIndex = state.slideIndex;
      newCurrentSlide = this.slides[this.currentSlideIndex];
      if (state.fragmentIndex === undefined) {
        this.currentFragmentIndex = -1;
      } else {
        this.currentFragmentIndex = state.fragmentIndex;
      }
    }
    if (newCurrentSlide) this.slideChangedCallbacks.forEach(callback => callback(newCurrentSlide));
    this.timeout = window.setTimeout(() => {
      this.slideTransitionEndCallbacks.forEach(callback => callback(newCurrentSlide));
    }, this.fastMode ? 100 : newCurrentSlide.duration);
    this.sync('setState');
  }

  extractState(manager: DataManager): undefined | {
    state: State<SlideId, StateId, Props, DataManager>;
    slide: DataSlide<SlideId, Props>
  } {
    for (const state of this.states) {
      if (state.on(manager)) {
        const slideIndex = this.slides.findIndex(slide => slide.id === state.slideId);
        if (slideIndex === -1) throw new Error(`slide not defined !`)
        const slide = this.slides[slideIndex];
        // const fragmentIndex = state.fragmentId ? this.slides[slideIndex].fragments.findIndex(f => f.id === state.fragmentId) : -1;
        return { state, slide };
        // return this.setState({ slideIndex: slideIndex, fragmentIndex: fragmentIndex });
      }
    }
  }

  inferState(manager: DataManager) {
    for (const state of this.states) {
      if (state.on(manager)) {
        const slideIndex = this.slides.findIndex(slide => slide.id === state.slideId);
        if (slideIndex === -1) throw new Error(`slide ${state.slideId} not defined !`)
        const fragmentIndex = state.fragmentId ? this.slides[slideIndex].fragments.findIndex(f => f.id === state.fragmentId) : -1;
        return this.setState({ slideIndex: slideIndex, fragmentIndex: fragmentIndex });
      }
    }
  }

  onFragment(fragmentId: string) {
    return {
      isVisible: computed<boolean>(() => {
        if (this.currentSlideIndex < 0 || this.currentSlideIndex >= this.slides.length) return false;
        const slide = this.slides[this.currentSlideIndex];
        // if (this.currentFragmentIndex < 0 || this.currentFragmentIndex >= slide.fragments.length) return false;
        const fragment = slide.fragments.find(fragment => fragment.id === fragmentId);
        if (!fragment) return false;
        return fragment.visible;
      }),
      isNotVisible: computed<boolean>(() => {
        if (this.currentSlideIndex < 0 || this.currentSlideIndex >= this.slides.length) return true;
        const slide = this.slides[this.currentSlideIndex];
        // if (this.currentFragmentIndex < 0 || this.currentFragmentIndex >= slide.fragments.length) return false;
        const fragment = slide.fragments.find(fragment => fragment.id === fragmentId);
        if (!fragment) return true;
        return !fragment.visible;
      })
    }
  }

  public get currentSlideId(): undefined | SlideId {
    if (this.currentSlideIndex < 0 || this.currentSlideIndex >= this.slides.length) return undefined;
    return this.slides[this.currentSlideIndex].id;
  }

  public onSlideChanged(callback: (slide: DataSlide<SlideId, Props>) => void) {
    this.slideChangedCallbacks.push(callback);
  }

  public onSlideTransitionEnd(callback: (slide: DataSlide<SlideId, Props>) => void) {
    this.slideTransitionEndCallbacks.push(callback);
  }

  public onFragmentShownById(fragmentId: string, callback: (fragment: DataFragment) => void) {
    this.fragmentShownCallbacks.push((fragment: DataFragment) => {
      if (fragment.id === fragmentId)
        callback(fragment)
    })
  }

  public onSlideTransitionEndById(id: SlideId, callback: (slide: DataSlide<SlideId, Props>) => void) {
    this.onSlideTransitionEnd(slide => {
      if (slide.id === id) callback(slide);
    })
  }

  public onSlideChangedById(id: SlideId, callback: (slide: DataSlide<SlideId, Props>) => void) {
    this.onSlideChanged(slide => {
      if (slide.id === id) callback(slide);
    })
  }

  public slide(slideOptions: SlideOptions<SlideId> | undefined = undefined): SlideProxy<SlideId> {
    if (slideOptions === undefined)
      return new SlideProxy<SlideId>(this, { index: this.currentSlideIndex });
    return new SlideProxy<SlideId>(this, slideOptions);
  }
}


export function usePretzel<State>(
  instance: PretzelManager,
  manager: DataManager,
  localesLoader: () => Promise<void>,
  afterManagerLoad: () => Promise<void>,
  afterRevealInitialized: () => Promise<void>
): { isInit: Ref<boolean> } {
  const isInit = ref(false);

  onMounted(async () => {
    await localesLoader();
    try {
      await manager.load();
    } finally {
      await afterManagerLoad();

      instance.initialize();
      // Reveal.initialize({
      //   controls: false,
      //   progress: false,
      //   slideNumber: false,
      //   keyboard: isInLocalDevelopmentMode(),
      //   disableLayout: true,
      //   overview: false,
      //   touch: false,
      //   navigationMode: 'linear',
      //   backgroundTransition: 'none',
      //   transition: 'fade',
      //   transitionSpeed: 'slow',
      //   viewDistance: 3,
      //   scrollProgress: true
      // });
      instance.inferState(manager);
      isInit.value = true;
      await afterRevealInitialized()
    }
  });

  return { isInit };
}
